import { Button, Col, Flex, Image, Space } from 'antd';
import 'bootstrap-icons/font/bootstrap-icons.min.css';
import React from 'react';

import styles from './evidence.styles';

/**
 * Define la estructura de un item de evidencia.
 */
export type EvidenceSchema = {
  /**
   * El tipo de evidencia (p. Ej., 'text', 'signature', 'photo').
   */
  type: string;
  /**
   * El valor de la evidencia. Para las fotos, puede ser una cadena de URLs separadas por comas.
   */
  value: string;
};

/**
 * Props para el componente Evidence.
 */
export type EvidenceProps = {
  /**
   * El nombre de la persona que firmó la entrega.
   */
  signName?: string;
  /**
   * La URL de la imagen de la firma.
   */
  signUrl?: string;
  /**
   * Un objeto que contiene la evidencia de la imagen, donde `value` es una cadena de URLs de imágenes separadas por comas.
   */
  imageEvidence?: EvidenceSchema;
  /**
   * Una URL opcional a un documento de prueba de entrega externo.
   */
  hrefUrl?: string;
};

/**
 * Un componente para mostrar evidencia de entrega, incluyendo firmas, fotos y enlaces.
 * Cuenta con una galería de imágenes con capacidades de vista previa y descarga.
 *
 * @param {EvidenceProps} props - Las props para el componente Evidence.
 *
 * @returns {JSX.Element} El componente Evidence renderizado.
 */
export const Evidence = ({ signName, signUrl, imageEvidence, hrefUrl }: EvidenceProps) => {
  const donwloadSignature = async () => {
    const url = signUrl ?? '';

    await getProxy(url, Date.now() + url.slice(url.lastIndexOf('.')));
  };

  return (
    <Flex style={styles.evidence} vertical>
      {hrefUrl && (
        <Flex justify='end'>
          <a href={hrefUrl} style={styles.evidence_link} target='_blank'>
            Ver prueba de entrega
          </a>
        </Flex>
      )}
      <Flex style={styles.signature}>
        {signUrl && (
          <Col>
            <Image.PreviewGroup
              preview={{
                toolbarRender: (_, { actions: { onRotateRight, onZoomOut, onZoomIn } }) =>
                  imgPreviewToolbar(
                    'Firma del Cliente',
                    onRotateRight,
                    onZoomOut,
                    onZoomIn,
                    donwloadSignature
                  )
              }}>
              <Image height={80} width={125} src={signUrl} style={styles.signature_img} />
            </Image.PreviewGroup>
          </Col>
        )}
        {signName && (
          <Col>
            <span className='font-semibold'>Firmado por</span>
            <p>{signName}</p>
          </Col>
        )}
      </Flex>
      {imageEvidence && ImgEvidence(imageEvidence)}
    </Flex>
  );
};

const ImgEvidence = (imageEvidence: EvidenceSchema) => (
  <Flex vertical gap={8}>
    <span className='font-semibold'>Fotos de entrega</span>
    {ImgCrousel(imageEvidence)}
  </Flex>
);

const imgPreviewToolbar = (
  title: string,
  onRotateRight: () => void,
  onZoomOut: () => void,
  onZoomIn: () => void,
  onDownload: () => void
) => (
  <Flex vertical align='center' gap={8}>
    <h5 style={styles.carousel_buttonSpace}>{title}</h5>
    <Space size={12} style={styles.carousel_buttonSpace}>
      <Button
        color='default'
        variant='filled'
        shape='circle'
        style={styles.carousel_button}
        icon={<i className='bi bi-zoom-in'></i>}
        onClick={onZoomIn}
      />
      <Button
        color='default'
        variant='filled'
        shape='circle'
        style={styles.carousel_button}
        icon={<i className='bi bi-zoom-out'></i>}
        onClick={onZoomOut}
      />
      <Button
        color='default'
        variant='filled'
        shape='circle'
        style={styles.carousel_button}
        icon={<i className='bi bi-arrow-clockwise'></i>}
        onClick={onRotateRight}
      />
      <Button
        color='default'
        variant='filled'
        shape='circle'
        style={styles.carousel_button}
        icon={<i className='bi bi-download'></i>}
        onClick={onDownload}
      />
    </Space>
  </Flex>
);

const getProxy = async (url: string, filename: string) => {
  try {
    const response = await fetch(`/api/proxy/download?url=${encodeURIComponent(url)}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = blobUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Error downloading image:', error);
  }
};

const ImgCrousel = (imageEvidence: EvidenceSchema) => {
  const [currentSlideIndex, setCurrentSlideIndex] = React.useState(0);
  const [selectedImageGlobalIndex, setSelectedImageGlobalIndex] = React.useState(0);

  const images = imageEvidence.value.split(',').filter((img) => img.trim() !== '');

  const onDownload = async () => {
    if (images.length === 0 || selectedImageGlobalIndex >= images.length) return;
    const url = images[selectedImageGlobalIndex];
    await getProxy(url, Date.now() + url.slice(url.lastIndexOf('.')));
  };

  return (
    <Flex vertical gap={16}>
      <Flex style={styles.carousel_imgGrid}>
        <Image.PreviewGroup
          preview={{
            toolbarRender: (_, { actions: { onRotateRight, onZoomOut, onZoomIn } }) =>
              imgPreviewToolbar(
                'Foto del Producto Entregado',
                onRotateRight,
                onZoomOut,
                onZoomIn,
                onDownload
              ),
            onChange: (indexWithinSlide) => {
              const globalIndex = currentSlideIndex * 5 + indexWithinSlide;
              setSelectedImageGlobalIndex(globalIndex);
            }
          }}>
          {images.map((element, img) => (
            <Image
              key={`${currentSlideIndex}-${img}-${element}`}
              height={'115px'}
              width={'calc(20% - 8px)'}
              style={styles.carousel_img}
              src={element}
            />
          ))}
        </Image.PreviewGroup>
      </Flex>
    </Flex>
  );
};
